import { useContext, useMemo } from 'react'
import { AppDataContext } from '../../../providers/AppData'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { SearchTypes } from '../../../providers/SearchProfilesPage/types'
import { TENANTS } from '../../../constants/global'
import SearchIconVariant2 from '../../Icons/SearchIcon'
import {
  Box,
  ExpandMoreIcon,
  LocationOnIcon,
  MyLocationIcon,
  PulseIcon,
  SearchBarLocationIcon,
  SearchBarSearchIcon,
  SearchIcon,
  Typography
} from '@hermes/web-components'
import { SearchType } from '../../SearchPage/common/types'
import ChevronRightIcon from '../../Icons/ChevronRightIcon'
import ArrowRightIcon from '../../Icons/ArrowRightIcon'
import { SearchProfilesPageContext } from '../../../providers/SearchProfilesPage'
import NextImage from 'next/image'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'

const useSearchBarConfig = () => {
  const { locale, tenantSettings } = useContext(AppDataContext)
  const {
    state: { filters }
  } = useContext(SearchProfilesPageContext)
  const { alternativeSearchEnabled } = useFeatureFlags()
  const translate = useTranslateMessage()

  const dentistryId = tenantSettings?.search?.DENTISTRY?.id.toString() || ''
  const careHomeId = tenantSettings?.search?.CARE_HOME?.id.toString() || ''
  const dentistrySlug = useMemo(
    () => (locale === TENANTS.DE || locale === TENANTS.AT ? 'zahn-mund-kieferheilkunde' : 'dentistry'),
    [locale]
  )
  const careHomeSlug = 'health-and-social-care'

  const searchTypes = useMemo(() => {
    const defaultSearchTypes: Array<SearchType> = [
      { title: translate('specialists'), value: SearchTypes.specialists, Icon: 'HeartPulseIcon' },
      { title: translate('practices'), value: SearchTypes.practices, Icon: 'ClinicIcon' }
    ]

    if (locale !== TENANTS.WPA) {
      defaultSearchTypes.push({
        title: translate('dentist'),
        value: SearchTypes.dentist,
        slug: dentistrySlug,
        Icon: 'DentistIcon',
        id: dentistryId
      })
      defaultSearchTypes.push({ title: translate('hospitals'), value: SearchTypes.hospital, Icon: 'HospitalIcon' })
    }

    if (locale === TENANTS.UK) {
      defaultSearchTypes.push({ title: translate('pharmacies'), value: SearchTypes.pharmacy, Icon: 'PharmaciesIcon' })
      const alternativeCareHomeProps = {
        title: translate('care-home'),
        value: SearchTypes.careHome,
        Icon: 'CareHomeIcon'
      }
      defaultSearchTypes.push(
        alternativeSearchEnabled
          ? alternativeCareHomeProps
          : { ...alternativeCareHomeProps, id: careHomeId, slug: careHomeSlug }
      )
      defaultSearchTypes.push({
        title: translate('home-care-specs'),
        value: SearchTypes.healthAndSocialCare,
        Icon: 'HomeCareSpecIcon'
      })
    }

    return defaultSearchTypes
  }, [careHomeId, dentistryId, dentistrySlug, locale, translate, alternativeSearchEnabled])

  const selectedSearchType = useMemo(
    () => searchTypes.find((st) => st.value === filters.searchType) || searchTypes[0],
    [filters.searchType, searchTypes]
  )

  const keywordsResultBubbleView = useMemo(() => locale === TENANTS.WPA, [locale])

  const MobileProfileTypeLeftIcon = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return (
          <div
            style={{
              marginLeft: '17px',
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <NextImage src={'/hermes-static/icons/HeartPulseIcon.svg'} alt="" width={30} height={22} objectFit="none" />
          </div>
        )
      }
      default: {
        return (
          <div
            style={{
              marginLeft: '17px',
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <NextImage
              src={`/hermes-static/icons/${selectedSearchType.Icon}.svg`}
              alt=""
              width={30}
              height={22}
              objectFit="none"
            />
          </div>
        )
      }
    }
  }, [locale, selectedSearchType.Icon])

  const MobileProfileTypeRightIcon = useMemo(() => {
    return ExpandMoreIcon
  }, [])

  const MobileKeywordSearchLeftIcon = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return SearchIconVariant2
      }
      default: {
        return PulseIcon
      }
    }
  }, [locale])

  const MobileKeywordSearchLeftIconAlternative = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return SearchIconVariant2
      }
      default: {
        return SearchBarSearchIcon
      }
    }
  }, [locale])

  const MobileKeywordSearchRightIcon = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return null
      }
      default: {
        return SearchIcon
      }
    }
  }, [locale])

  const MobileLocationSearchLeftIcon = useMemo(() => {
    return LocationOnIcon
  }, [])

  const MobileLocationSearchLeftIconAlternative = useMemo(() => {
    return SearchBarLocationIcon
  }, [])

  const MobileLocationSearchRightIcon = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return ExpandMoreIcon
      }
      default: {
        return MyLocationIcon
      }
    }
  }, [locale])

  const { SearchIconVariant, searchIconPosition } = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return {
          SearchIconVariant: SearchIconVariant2,
          searchIconPosition: 'startAdornment'
        }
      }
      default: {
        return {
          SearchIconVariant: SearchIcon,
          searchIconPosition: 'startAdornment'
        }
      }
    }
  }, [locale])

  const ModalBackButtonIcon = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return () => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'rotate(180deg)',
              backgroundColor: 'white',
              borderRadius: '32px',
              width: '40px',
              height: '40px'
            }}
          >
            <ArrowRightIcon sx={{ transform: 'scale(0.65)' }} />
          </Box>
        )
      }
      default: {
        return () => (
          <>
            <ChevronRightIcon reflectIfRtl sx={{ transform: 'scaleX(-1)', ml: -1 }} />
            <Typography>{translate('back')}</Typography>
          </>
        )
      }
    }
  }, [locale, translate])

  return {
    searchTypes,
    selectedSearchType,
    keywordsResultBubbleView,
    MobileProfileTypeLeftIcon,
    MobileProfileTypeRightIcon,
    MobileKeywordSearchLeftIcon,
    MobileKeywordSearchLeftIconAlternative,
    MobileKeywordSearchRightIcon,
    MobileLocationSearchLeftIcon,
    MobileLocationSearchLeftIconAlternative,
    MobileLocationSearchRightIcon,
    SearchIcon: SearchIconVariant,
    searchIconPosition,
    ModalBackButtonIcon
  }
}

export default useSearchBarConfig
